<template>
  <div class="license-information mt-1">
    <validation-observer ref="formValidator">
      <div class="row body pt-4">
        <div class="col">
          <div class="col-xs-12">
            <div class="container-fluid pdr-0 mgr-0">
              <div class="row ml-2 pdr-0 mgr-0">
                <div class="col-12">
                  <label class="data-label">
                    ¿La Licencia es Provisional?
                  </label>
                </div>
                <div class="col">
                  <validation-provider v-slot="{ errors }" rules="required">
                    <custom-radio
                      :key="provisionalFlag"
                      v-model="provisionalFlag"
                      custom-error-msg="Favor de seleccionar si la licencia es provisional o no"
                      :error="errors[0]"
                      is-inline
                      name="rbRecurrenceType"
                      :options="provisionalOptions"
                      @input="provisionalFlagChanged"
                    />
                  </validation-provider>
                </div>
              </div>
              <div class="row ml-2 pdr-0 mgr-0">
                <div class="col-sm-12 col-md-6 col-lg-5 mb-3">
                  <validation-provider
                    v-slot="{ errors }"
                    name="LicenseType"
                    :rules="{ required: true, notEmptyObject: 'id' }"
                  >
                    <custom-drop-down
                      v-model="selectedSpecialty"
                      custom-error-msg="Favor de seleccionar una Especialidad"
                      :disabled="license.specialistLicenseId != null"
                      :error="errors[0]"
                      fieldkey="id"
                      fieldname="name"
                      label="Especialidad:"
                      :options="filteredSpecialtyTypes"
                      placeholder="Seleccione"
                      :reduce="(o) => ({ id: o.id, name: o.name })"
                      @input="onChangeSpecialtyHandler"
                    />
                  </validation-provider>
                </div>
                <div v-if="hasDetail" class="col-sm-12 col-md-6 col-lg-4 mb-3">
                  <validation-provider
                    v-slot="{ errors }"
                    name="LicenseNo"
                    rules="required"
                  >
                    <custom-input
                      v-model="specialistLicense.licenseNo"
                      :custom-error-msg="validationMessages.licenseNo"
                      :disabled="cantEditLicenseNo"
                      :error="errors[0]"
                      label="Número de Licencia:"
                    />
                  </validation-provider>
                </div>
                <div v-if="hasDetail" class="col-sm-12 col-md-4 col-lg-3 mb-3">
                  <validation-provider
                    v-slot="{ errors }"
                    name="LicenseExpireDate"
                    rules="required"
                  >
                    <custom-date-picker
                      v-model="specialistLicense.licenseExpireDate"
                      :custom-error-msg="validationMessages.licenseExpireDate"
                      :error="errors[0]"
                      label="Fecha de Expiración:"
                      :min-date="new Date()"
                    />
                  </validation-provider>
                </div>
                <div v-if="hasDetail" class="col-sm-12 col-md-6 col-lg-3 mb-3">
                  <validation-provider
                    v-slot="{ errors }"
                    name="AgeRange"
                    :rules="{ required: true, notEmptyObject: 'id' }"
                  >
                    <custom-drop-down
                      v-model="selectedAgeRange"
                      :custom-error-msg="validationMessages.ageRange"
                      :error="errors[0]"
                      fieldkey="id"
                      fieldname="name"
                      label="Rango de Edad:"
                      :options="ageRanges"
                      placeholder="Seleccione"
                      :reduce="(o) => ({ id: o.id, name: o.name })"
                    />
                  </validation-provider>
                </div>
                <div
                  v-if="hasDetail && showSupervisorCheck"
                  class="col-sm-6 col-md-4 col-lg-2 mb-3"
                >
                  <check-box
                    v-model="specialistLicense.supervisorFlag"
                    class="checkbox"
                    text="Supervisor"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="pl-4">
        <license-documents
          v-model="specialistLicense"
          @delete-document="deleteDocument"
          @download-document="downloadDocument"
        />
      </div>
      <div class="row">
        <div class="col">
          <action-button
            class="py-2 mt-4 float-right"
            i="fas fa-plus"
            text="Guardar"
            variant="success"
            @click="save"
          />
          <action-button
            class="mr-3 mt-4 py-2 btnCancel float-right"
            i="fas fa-times-circle"
            text="Cancelar"
            variant="outline-danger"
            @click="cancel"
          />
        </div>
      </div>
      <error
        v-if="customErrorMessage != null"
        :message="customErrorMessage"
        variant="no-border"
      />
    </validation-observer>
  </div>
</template>

<script>
import CustomInput from '@/components/common/form/CustomInput';
import CustomDropDown from '@/components/common/form/CustomDropDown';
import CustomDatePicker from '@/components/common/form/CustomDatePicker';
import LicenseDocuments from './LicenseDocuments';
import { serialize as objectToFormData } from 'object-to-formdata';
import CustomRadio from 'custom-radio';
import {
  getSpecialistServiceTypes,
  getSpecialistSpecialtyTypes,
  getSpecialistAgeRange,
  createLicense,
  editLicense,
  licenseCanSupervise,
  getSpecialistLicenseDocuments,
  deleteSpecialistLicenseDocument,
} from '@/modules/provider/api';
import generalService from '@/modules/app/api';
import Error from '@/components/common/Error';
import alert from '@/mixins/alert.mixin';
import { opMode } from '@/components/common/recurrence/utils/enums';
import specialistSpecialtyTypes from '@/utils/constants/specialistSpecialtyTypes.js';
import SpecialtyTypes from '@/utils/constants/specialtyTypes.js';
export default {
  name: 'LicenseInformationAddEdit',
  components: {
    Error,
    CustomInput,
    CustomDropDown,
    CustomDatePicker,
    LicenseDocuments,
    CustomRadio,
  },
  mixins: [alert],
  model: {
    prop: 'license',
    event: 'on-saved',
  },
  props: {
    license: {
      type: Object,
      default: () => ({
        id: null,
        licenseNo: null,
        licenseExpireDate: null,
        licenseTypeId: null,
        ageRange: null,
        ageRangeId: null,
        ageRangeName: null,
        isProvisional: false,
        isSupervisor: false,
        documents: [],
        provisionalFlag: null,
      }),
    },
    isATEvaluator: {
      type: [Object, Boolean],
    },
    activateSpecialistLicenseFilterByHealthDepartment: {
      type: Boolean,
      default: false,
    },
    isRelatedMaster: {
      type: Boolean,
      default: false,
    },
    isCseeOfficial: {
      type: Boolean,
      default: false,
    },
    immediateSave: {
      type: Boolean,
      required: true,
    },
    //ECG:I = insert mode, E = edit mode, crear objeto congelado con esto?
    opMode: {
      type: String,
      default: () => '',
    },
    selectedLicenses: {
      type: Array,
      default: () => [],
    },
    customErrorMessage: {
      type: String,
      default: () => null,
    },
    specialistLicenseDocumentTypes: {
      type: Array,
      default: () => [],
    },
    specialistCompanyId: {
      type: Number,
      default: () => null,
    },
  },
  data() {
    return {
      provisionalFlag: null,
      provisionalFlagIndex: 0,
      notProvisionalFlag: false,
      specialistLicense: { ...this.license },
      documents: [],
      specialtyTypes: [],
      ageRanges: [],
      selectedSpecialty: { id: 0, name: '' },
      selectedAgeRange: { id: 0, name: '' },
      validationMessages: {
        serviceType: 'Favor seleccionar un tipo servicio.',
        disciplineType: 'Favor seleccionar un servicio.',
        ageRange: 'Favor de seleccionar un Rango de Edad.',
        licenseNo: 'Favor de documentar un Número de Licencia.',
        licenseExpireDate: 'Favor de seleccionar una Fecha de Expiración.',
      },
      supervisorCheck: null,
      provisionalOptions: [
        { name: 'answerYes', label: 'Sí', value: true },
        { name: 'answerNo', label: 'No', value: false },
      ],
    };
  },
  computed: {
    fiscalYearId() {
      return this.$route.params.fiscalYearId;
    },
    companyId() {
      return this.specialistCompanyId ?? this.$route.params.companyId;
    },
    filteredSpecialtyTypes() {
      let filtered = this.specialtyTypes;

      if (this.isRelatedMaster) {
        filtered = filtered.filter((s) => s.id == 8); //8 Certificación de Asistencia Tecnológica
      }

      if (this.isCseeOfficial) {
        filtered = filtered.filter(
          (s) => s.id == specialistSpecialtyTypes.FuncionarioDEPR
        );
      } else {
        filtered = filtered.filter(
          (s) => s.id != specialistSpecialtyTypes.FuncionarioDEPR
        );
      }

      if (this.isATEvaluator.value) {
        filtered = filtered.filter(
          (s) => s.id !== specialistSpecialtyTypes.AsistenciaTecnologica
        );
      } else {
        filtered = filtered.filter(
          (s) => s.id !== specialistSpecialtyTypes.EvaluadorAutorizadoAT
        );
      }

      filtered = filtered.filter(
        (s) => !this.selectedLicenses.find((sl) => sl.specialtyTypeId == s.id)
      );
      if (
        this.activateSpecialistLicenseFilterByHealthDepartment &&
        !this.specialistLicense.provisionalFlag
      ) {
        filtered = filtered.filter((s) => !s.isFromHD);
      }
      return filtered;
    },
    hasDetail() {
      return (
        !this.selectedSpecialty ||
        (this.selectedSpecialty.id != SpecialtyTypes.ServicioEducativo &&
          this.selectedSpecialty.id != SpecialtyTypes.AsistenteServicio &&
          this.selectedSpecialty.id != SpecialtyTypes.FuncionarioDEPR)
      );
    },
    cantEditLicenseNo() {
      return !(
        this.opMode == opMode.INSERT ||
        (this.opMode == opMode.EDIT &&
          this.userHasPermissions('provider.specialist.edit.nolicense'))
      );
    },
    showSupervisorCheck() {
      return this.supervisorCheck;
    },
  },
  watch: {
    selectedSpecialty(val) {
      this.specialistLicense.specialtyTypeId = val.id;
      this.specialistLicense.specialtyTypeName = val.name;
    },
    selectedAgeRange(val) {
      this.specialistLicense.ageRangeCd = val.id;
      this.specialistLicense.ageRangeName = val.name;
    },
    specialistLicenseDocumentTypes: {
      immediate: true,
      handler() {
        this.refreshDocuments();
      },
    },
  },
  created() {
    getSpecialistServiceTypes().then((resp) => {
      this.serviceTypes = resp.data;
    });
    getSpecialistAgeRange().then((resp) => {
      this.ageRanges = resp.data;
    });
    getSpecialistSpecialtyTypes().then((resp) => {
      this.specialtyTypes = resp.data;
    });
    this.getLicenseDocuments();
  },
  async mounted() {
    this.provisionalFlag = this.provisionalOptions.find(
      (x) => x.value === this.license.provisionalFlag
    );

    if (this.specialistLicense.specialtyTypeId) {
      this.selectedSpecialty = {
        id: this.specialistLicense.specialtyTypeId,
        name: this.specialistLicense.specialtyTypeName,
      };
      await this.isAllowedToSupervise(this.specialistLicense.specialtyTypeId);
    }
    if (this.specialistLicense.ageRangeCd) {
      this.selectedAgeRange = {
        id: this.specialistLicense.ageRangeCd,
        name: this.specialistLicense.ageRangeName,
      };
    }
  },
  methods: {
    provisionalFlagChanged() {
      this.specialistLicense.provisionalFlag = this.provisionalFlag.value;
    },
    getLicenseDocuments() {
      getSpecialistLicenseDocuments(
        this.specialistLicense?.specialistLicenseId
      ).then((resp) => {
        this.documents = resp.data;
        this.refreshDocuments();
      });
    },
    downloadDocument({ documentId, attachment }) {
      if (documentId) generalService.downloadDocumentByAttachmentId(documentId);
      else {
        let reader = new FileReader();
        reader.readAsDataURL(attachment);
        reader.onloadend = () => {
          let a = document.createElement('a');
          document.body.appendChild(a);
          a.style = 'display: none';
          const url = reader.result;
          a.href = url;
          a.download = attachment.name;
          a.click();
          window.URL.revokeObjectURL(url);
        };
      }
    },
    async deleteDocument(document) {
      if (document.specialistLicenseDocumentId > 0) {
        deleteSpecialistLicenseDocument(document.specialistLicenseDocumentId)
          .then(() => {
            this.ShowToast(
              'Proceso Exitoso',
              'Documento eliminado exitosamente.',
              'success'
            );
            this.getLicenseDocuments();
          })
          .catch((error) => {
            this.ShowToast('Error', error.response.data.message, 'error');
          });
        return;
      }
      document.attachment = null;
      document.documentFilename = null;
      document.guid = null;
      document.licenseDocumentExpiryDate = null;
    },
    refreshDocuments() {
      const documents = this.specialistLicenseDocumentTypes.map((x) => {
        const document = this.documents.find(
          (d) => d.specialistLicenseDocumentTypeId === x.id
        ); // to do, make sure this is the right document by its type
        if (document) {
          document.licenseDocumentExpiryDate = document.expirationDate
            ? new Date(document.expirationDate)
            : null;
          return document;
        }
        return {
          specialistLicenseDocumentId: null,
          specialistLicenseId: null,
          specialistLicenseDocumentTypeId: x.id,
          specialistLicenseDocumentTypeName: x.name,
          documentId: null,
          attachment: null,
          licenseDocumentExpiryDate: null,
          guid: null,
        };
      });
      this.specialistLicense.documents = documents;
    },
    async save() {
      var self = this;

      const isValid = await this.$refs.formValidator.validate();

      if (isValid) {
        if (self.immediateSave) {
          let promise = null;
          const documentsPayload = this.specialistLicense.documents
            .filter((doc) => Boolean(doc.attachment))
            .map((doc) => ({
              ...doc,
              expirationDate: doc?.licenseDocumentExpiryDate
                ? doc?.licenseDocumentExpiryDate?.toISOString()?.split('T')[0]
                : null,
            }));

          const payload = {
            ...self.specialistLicense,
            documents: documentsPayload,
            companyId: this.companyId,
            fiscalYearId: this.fiscalYearId,
          };

          const params = objectToFormData(
            { ...payload },
            {
              indices: true,
              nullsAsUndefineds: true,
              dotsForObjectNotation: true,
            }
          );

          if (self.opMode == opMode.EDIT) {
            promise = editLicense(params);
          } else if (self.opMode == opMode.INSERT) {
            promise = createLicense(params);
          }

          await promise
            .then((res) => {
              self.ShowToast(
                'Proceso Exitoso',
                'Licencia guardada exitosamente.',
                'success'
              );
              self.specialistLicense = res.data;
              self.$emit('on-saved', self.specialistLicense);
            })
            .catch((error) => {
              self.ShowToast('Error', error.response.data.message, 'error');
            });
        } else {
          self.$emit('on-saved', self.specialistLicense);
        }
      } else {
        //TODO: Use the stored one
        self.ShowToast('Error', 'Información incompleta. (190803)', 'error');
      }
    },
    async cancel() {
      this.$emit('on-cancel');
    },
    async isAllowedToSupervise(specialtyId) {
      licenseCanSupervise(specialtyId).then((resp) => {
        this.supervisorCheck = resp.data;
      });
    },
    async onChangeSpecialtyHandler(specialty) {
      await this.isAllowedToSupervise(specialty.id);
    },
  },
};
</script>
<style scoped>
@media (min-width: 576px) {
  .license-information .btnCancel {
    float: left;
  }
  .actions {
    padding-left: 35px;
  }
}

@media (min-width: 768px) {
  .license-information .btnCancel {
    float: inherit;
  }
}

@media (min-width: 992px) {
}

@media (min-width: 1200px) {
}

.checkbox {
  margin-top: 33px !important;
  margin-left: 35%;
}
@media (max-width: 768px) {
  .checkbox {
    margin-top: 15px !important;
    margin-left: 20%;
  }
}

.input-group.checkbox {
  margin-left: 0px;
  margin-top: 3px !important;
  margin-bottom: 18px;
}
</style>
